import Image from '../../image';
import Button from '../../button';
import { useTranslations } from '@fshr/context/i18n-context';
import { useState, useMemo } from 'react';
import ErrorAlert from '../../error-alert';
import { RoomUpgradeBodyRequest, RoomUpgradeRecommendationsType } from '../../types';
import { cn, getConfig } from '@fshr/shared-react';
import CloseAlt from '../../icon/close-alt';
import { defaultFetcher } from '@fshr/shared';
import { observer } from 'mobx-react-lite';
import { CartStore } from '@micro-apps/stores';
import LoadingIndicator from '../../loading-indicator';
import { useRecoTracking } from '../../for-you/analytics/recoTracker';
import { useRecoImpressionBatchObserver } from '../../for-you/hooks/useRecoImpression';

type UpgradeRoomRecommendationProps = {
	isLoading: boolean;
	roomUpgradeRecommendation?: RoomUpgradeRecommendationsType;
	error: unknown;
	bookingId?: string;
	cartStore: CartStore;
	placement?: string;
	trackRecoImpressions?: boolean;
};

export const UpgradeRoomRecommendationCard = observer(
	({
		error,
		isLoading,
		roomUpgradeRecommendation,
		bookingId,
		cartStore,
		placement,
		trackRecoImpressions,
	}: UpgradeRoomRecommendationProps) => {
		const t = useTranslations();
		const [showRecommendation, setShowRecommendation] = useState(true);
		const [errorOnUpgradeRoom, setErrorOnUpgradeRoom] = useState(false);
		const { trackPropertyRecoClick } = useRecoTracking();

		const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();

		const handleCloseRecommendation = () => {
			setShowRecommendation(false);
		};

		const canRenderCard = Boolean(
			showRecommendation &&
				!isLoading &&
				!errorOnUpgradeRoom &&
				!error &&
				!roomUpgradeRecommendation?.bffError &&
				roomUpgradeRecommendation?.recommendation &&
				Boolean(bookingId) &&
				roomUpgradeRecommendation?.roomUpgradeAvailabilityInformation
		);

		const impressionPayload = useMemo(() => {
			if (!canRenderCard) return null;

			return {
				reco_placement: placement ?? 'shopping_cart-room_upgrade',
				reco_position: '1',
				property_datalake_reco_id: 'na',
				reco_room_code_array: 'na',
				reco_room_package_code_array: 'na',
			};
		}, [canRenderCard, placement]);

		const impressionRef = useRecoImpressionBatchObserver(
			impressionPayload,
			'property',
			trackRecoImpressions
		);

		if (cartStore.fetching) {
			return <LoadingIndicator />;
		}

		const handleUpgradeRoom = async () => {
			trackPropertyRecoClick({
				propertyCode: 'na',
				propertyName: 'na',
				propertyDatalakeRecoId: 'na',
				placement: placement ?? 'shopping_cart-room_upgrade',
				position: 1,
			});
			cartStore.setFetching(true);
			try {
				if (!bookingId) {
					console.error('No booking id provided');
					return;
				}

				if (!roomUpgradeRecommendation?.roomUpgradeAvailabilityInformation) {
					console.error('Room availability information not provided');
					return;
				}

				const bodyObject: RoomUpgradeBodyRequest = {
					resultId: roomUpgradeRecommendation.roomUpgradeAvailabilityInformation.resultId,
					resultSetId: roomUpgradeRecommendation.roomUpgradeAvailabilityInformation.resultSetId,
				};

				await defaultFetcher(`${NEXT_PUBLIC_BFF_BASE_URL}/cart/${bookingId}`, {
					method: 'POST',
					body: JSON.stringify(bodyObject),
				});

				await cartStore.forceFetchBooking();

				setShowRecommendation(false);
			} catch (error) {
				console.error(error);
				setErrorOnUpgradeRoom(true);
				cartStore.setFetching(false);
			}
		};

		if (!showRecommendation) {
			return null;
		}

		if (isLoading) {
			return (
				<div className="w-full px-[10px]">
					<div
						className={cn(
							'w-full animate-pulse border-0 bg-neutral-200 h-[140px] relative overflow-hidden'
						)}
					/>
				</div>
			);
		}

		if (errorOnUpgradeRoom) {
			return (
				<div className="w-full padding-inline-l">
					<ErrorAlert
						showCloseIcon
						message={t('Sorry, this room is no longer available for selection.')}
					/>
				</div>
			);
		}

		if (roomUpgradeRecommendation?.bffError) {
			if (error && !isLoading) console.error(error);
			return null;
		}

		// Missing required data to render the card
		if (
			!bookingId ||
			!roomUpgradeRecommendation?.recommendation ||
			!roomUpgradeRecommendation?.roomUpgradeAvailabilityInformation
		) {
			return null;
		}

		if (error && !isLoading) {
			console.error(error);
			return null;
		}

		if (roomUpgradeRecommendation?.bffError) return null;

		if (!canRenderCard) return null;

		return (
			<div
				ref={impressionRef}
				className="w-full flex flex-row p-[30px] spacing-inline-s items-start justify-start bg-surface-raised-subtle"
			>
				<div className="flex w-[100px] h-[100px] min-w-[100px] min-h-[100px] flex-shrink-0">
					<Image
						roughSize="s"
						src={roomUpgradeRecommendation?.recommendation?.image.url ?? ''}
						alt={t('Upgrade Room Recommendation Card')}
						className="h-full w-full object-cover"
					/>
				</div>
				<div className="relative flex flex-col gap-[15px]">
					<div className="flex spacing-inline-s">
						<div>
							<h2 className="text-subtitle1 text-text-primary">
								{t('Would you like to upgrade your room to {0}?', [
									roomUpgradeRecommendation?.recommendation?.name ?? '',
								])}
							</h2>
						</div>
						<div>
							<Button
								intent="unstyled"
								onClick={handleCloseRecommendation}
								aria-label={t('Close upgrade room recommendation')}
							>
								<CloseAlt width={15} height={15} />
							</Button>
						</div>
					</div>
					<Button
						type="button"
						onClick={handleUpgradeRoom}
						className="w-[160px] h-[35px]"
						intent="outline"
					>
						{t('Upgrade')}
					</Button>
				</div>
			</div>
		);
	}
);
